'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Building2, Users, MapPin, Sprout, ShoppingBag, GraduationCap, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import { FadeUp } from '@/components/ui/fade-up'

const products = [
  {
    id: 'municipal-crm',
    icon: Building2,
    title: '自治体向けCRM',
    category: '行政DX',
    description: '住民データの一元管理と窓口業務の効率化を実現。マイナンバーカードと連携し、各種申請のオンライン化を支援します。',
    features: [
      '住民情報の一元管理',
      'オンライン申請システム',
      'マイナンバー連携',
      'AI問い合わせ対応'
    ],
    badge: '導入実績No.1',
    caseStudy: '広島市で窓口待ち時間を70%削減'
  },
  {
    id: 'tourism-dx',
    icon: MapPin,
    title: '観光DXプラットフォーム',
    category: '観光・地域振興',
    description: '観光客の行動データ分析から、パーソナライズされた観光体験の提供まで。地域の魅力を最大化するDXソリューション。',
    features: [
      '観光客行動分析',
      'デジタル観光パス',
      'AR観光ガイド',
      '多言語対応'
    ],
    badge: null,
    caseStudy: '尾道市で観光客満足度30%向上'
  },
  {
    id: 'regional-portal',
    icon: Users,
    title: '地域ポータルアプリ',
    category: '地域コミュニティ',
    description: '地域の情報発信から住民同士の交流まで。デジタルで地域コミュニティを活性化するプラットフォーム。',
    features: [
      '地域情報配信',
      'イベント管理',
      'ポイント制度',
      '防災情報連携'
    ],
    badge: null,
    caseStudy: '呉市で住民参加率が2倍に'
  },
  {
    id: 'smart-agriculture',
    icon: Sprout,
    title: 'スマート農業システム',
    category: '農業DX',
    description: 'IoTセンサーとAI分析で、農作業の最適化と収穫量の向上を実現。持続可能な農業を支援します。',
    features: [
      'IoT環境モニタリング',
      'AI収穫予測',
      '自動灌水制御',
      '作業記録管理'
    ],
    badge: '特許取得',
    caseStudy: '世羅町で収穫量20%向上'
  },
  {
    id: 'local-commerce',
    icon: ShoppingBag,
    title: '地域商店街DX',
    category: '商業振興',
    description: '地域商店街のデジタル化を支援。オンラインとオフラインを融合した新しい買い物体験を提供。',
    features: [
      'デジタル商品券',
      'オンライン商店街',
      '在庫一元管理',
      'データ分析'
    ],
    badge: null,
    caseStudy: '福山市商店街で売上15%増'
  },
  {
    id: 'education-dx',
    icon: GraduationCap,
    title: '教育DXプラットフォーム',
    category: '教育',
    description: '地域の教育機関向けDXソリューション。オンライン学習と対面授業を効果的に組み合わせた学習環境を提供。',
    features: [
      'オンライン授業配信',
      '学習進捗管理',
      'AI個別指導',
      '保護者連携'
    ],
    badge: '新製品',
    caseStudy: '三原市で学習効果25%向上'
  }
]

export function ProductsContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-[#BEB0A8] py-12 md:py-16">
        <div className="container animate-fade-up">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 font-['Noto_Sans_JP',_sans-serif] bg-[#292623] text-white">地域課題に特化した製品群</Badge>
            <h1 className="font-['Montserrat',_sans-serif] text-[32px] md:text-[46px] font-semibold text-[#292623] tracking-[-0.92px] mb-6">
              製品一覧
            </h1>
            <p className="font-['Noto_Sans_JP',_sans-serif] text-[16px] md:text-[18px] text-[#292623]/80">
              地域の課題解決に特化した、実績あるDXソリューション。<br />
              それぞれの地域に最適な形でカスタマイズ可能です。
            </p>
          </div>
        </div>
      </section>

      {/* フィルターセクション */}
      <section className="py-6 md:py-8 bg-white border-b border-[#292623]/10">
        <div className="container px-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {['すべて', '行政DX', '観光・地域振興', '地域コミュニティ', '農業DX', '商業振興', '教育'].map((category) => (
              <Button
                key={category}
                variant={category === 'すべて' ? 'default' : 'outline'}
                size="sm"
                className={category === 'すべて' ? 'bg-[#292623] text-white font-["Noto_Sans_JP",_sans-serif]' : 'border-[#292623]/30 text-[#292623] font-["Noto_Sans_JP",_sans-serif] hover:bg-[#BEB0A8]/20'}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* 製品グリッド */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => {
              const Icon = product.icon
              return (
                <FadeUp key={product.id} delay={index * 0.1}>
                  <Card className="h-full hover:shadow-lg transition-shadow bg-white border-[#292623]/10">
                    <CardHeader>
                      <div className="flex items-start justify-between mb-4">
                        <Icon className="h-12 w-12 text-[#292623]" />
                        {product.badge && (
                          <Badge variant="secondary" className="bg-[#BEB0A8]/30 text-[#292623] font-['Noto_Sans_JP',_sans-serif]">{product.badge}</Badge>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Badge variant="outline" className="text-xs border-[#292623]/30 text-[#292623] font-['Noto_Sans_JP',_sans-serif]">
                          {product.category}
                        </Badge>
                        <CardTitle className="font-['Montserrat',_sans-serif] text-[20px] md:text-[22px] text-[#292623]">{product.title}</CardTitle>
                        <CardDescription className="font-['Noto_Sans_JP',_sans-serif] text-[13px] md:text-[14px] text-[#292623]/70">{product.description}</CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="font-['Noto_Sans_JP',_sans-serif] text-sm font-semibold mb-2 text-[#292623]">主な機能</p>
                        <ul className="space-y-1">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623]/70">
                              <span className="text-[#292623] mr-2">•</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-2 border-t border-[#292623]/10">
                        <p className="font-['Noto_Sans_JP',_sans-serif] text-sm text-[#292623] font-medium">
                          {product.caseStudy}
                        </p>
                      </div>
                      <Button asChild className="w-full bg-[#292623] text-white hover:bg-[#292623]/90 font-['Noto_Sans_JP',_sans-serif]">
                        <Link href={`/products/${product.id}`}>
                          詳細を見る
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              地域の課題に合わせた最適な製品をご提案
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              導入事例や詳細な機能について、お気軽にお問い合わせください。<br />
              専門スタッフが貴地域に最適なソリューションをご提案いたします。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg">
                製品資料をダウンロード
              </Button>
              <Button size="lg" variant="outline">
                導入相談を申し込む
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}